import { useEffect, useState, useCallback } from "react";
import { api, openPdf } from "@/lib/api";
import { useFiscalYear } from "@/context/FiscalYearContext";
import { PageHeader, Loading, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { FileDown } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const COLORS = {
  green: { dot: "bg-green-500", label: "完了", row: "" },
  yellow: { dot: "bg-amber-500", label: "実施済・記録不足", row: "bg-amber-50/50" },
  red: { dot: "bg-rose-500", label: "未実施", row: "bg-rose-50/50" },
};

export default function AuditMode() {
  const { fiscalYear } = useFiscalYear();
  const [data, setData] = useState(null);
  const [offices, setOffices] = useState([]);
  const [officeId, setOfficeId] = useState("all");

  const load = useCallback(() => {
    if (!fiscalYear) return;
    let url = `/audit-mode?fiscal_year=${fiscalYear}`;
    if (officeId !== "all") url += `&office_id=${officeId}`;
    api.get(url).then(({ data }) => setData(data));
  }, [fiscalYear, officeId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/offices").then(({ data }) => setOffices(data)); }, []);

  if (!data) return <Loading />;
  const s = data.summary;

  return (
    <div>
      <PageHeader title="実地指導・監査モード" subtitle={`${fiscalYear}年度 必要な研修・委員会・訓練の実施/記録状況`}
        actions={
          <div className="flex items-center gap-2">
            <Select value={officeId} onValueChange={setOfficeId}>
              <SelectTrigger className="w-48" data-testid="audit-office-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">全事業所</SelectItem>{offices.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button variant="outline" onClick={() => openPdf(`/reports/audit?fiscal_year=${fiscalYear}${officeId !== "all" ? `&office_id=${officeId}` : ""}`)} data-testid="audit-pdf-btn"><FileDown className="w-4 h-4 mr-1" /> PDF出力</Button>
          </div>
        } />

      <div className="grid grid-cols-3 gap-4 mb-6">
        {[["green", s.green], ["yellow", s.yellow], ["red", s.red]].map(([c, v]) => (
          <div key={c} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3" data-testid={`audit-summary-${c}`}>
            <span className={`w-3 h-3 rounded-full ${COLORS[c].dot}`} />
            <div><div className="text-2xl font-bold text-slate-900">{v}</div><div className="text-xs text-slate-400">{COLORS[c].label}</div></div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {data.rows.length ? (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs">
              <tr><th className="text-left p-3">状態</th><th className="text-left p-3">区分</th><th className="text-left p-3">名称</th><th className="text-left p-3">事業所</th><th className="text-center p-3">実施</th><th className="text-center p-3">記録</th><th className="text-center p-3">修了率</th></tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={i} className={`border-t border-slate-100 ${COLORS[r.color].row}`} data-testid={`audit-row-${i}`}>
                  <td className="p-3"><span className={`inline-block w-3 h-3 rounded-full ${COLORS[r.color].dot}`} /></td>
                  <td className="p-3 text-slate-500">{r.category}</td>
                  <td className="p-3 font-medium text-slate-800">{r.name}</td>
                  <td className="p-3 text-slate-500">{r.office_name}</td>
                  <td className="p-3 text-center">{r.implemented ? "✓" : "—"}</td>
                  <td className="p-3 text-center">{r.recorded ? "✓" : "—"}</td>
                  <td className="p-3 text-center">{r.completion_rate != null ? `${r.completion_rate}%` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <EmptyState title="対象データがありません" />}
      </div>
    </div>
  );
}
